// Environment Configuration
// This file manages all environment variables and API configuration

export interface AppConfig {
  apiBaseUrl: string
  appName: string
  appVersion: string
  isDevelopment: boolean
  isProduction: boolean
  googleMapsApiKey?: string
}

// Get environment variable with fallback
const getEnvVar = (key: string, fallback: string = ''): string => {
  return import.meta.env[key] || fallback
}

// Main configuration object
export const config: AppConfig = {
  // API Base URL - this is where you configure your remote server
  apiBaseUrl: getEnvVar('VITE_API_BASE_URL', 'http://localhost:5239'),
  
  // App metadata
  appName: getEnvVar('VITE_APP_NAME', 'Coast Smoke Alarms PWA'),
  appVersion: getEnvVar('VITE_APP_VERSION', '1.0.0'),
  
  // Environment flags
  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
  
  // Google Maps API Key
  googleMapsApiKey: 'AIzaSyAO7FNBf1tBvGh8FFvdJ3avBmq7nHKtgXg',
}

// API Endpoints builder
export const apiEndpoints = {
  // Base API URL
  base: config.apiBaseUrl,
  // Authentication
  auth: {
    login: `${config.apiBaseUrl}/api/auth/login`,
    logout: `${config.apiBaseUrl}/api/auth/logout`,
    validate: `${config.apiBaseUrl}/api/auth/validate`,
    refresh: `${config.apiBaseUrl}/api/auth/refresh`,
  },
  
  // Work Orders
  workOrders: {
    base: `${config.apiBaseUrl}/api/workorderdetails`,
    technician: (technicianId: number) => `${config.apiBaseUrl}/api/technician/${technicianId}/workorders`,
    filter: `${config.apiBaseUrl}/api/workorderdetails/filter`,
    details: (workOrderId: number) => `${config.apiBaseUrl}/api/workorderdetails/${workOrderId}/technician`,
    update: (workOrderId: number) => `${config.apiBaseUrl}/api/workorderdetails/${workOrderId}/technician`,
    runsheet: (technicianId: number, date: string) => `${config.apiBaseUrl}/api/runsheet/technician/${technicianId}?date=${date}`,
    getPhotos: (workOrderId: number) => `${config.apiBaseUrl}/api/workorder/photos/workorder/${workOrderId}`,
    mapLocations: `${config.apiBaseUrl}/api/workorderdetails/map-locations`,
  },

  // Work Order Notes
  workOrderNotes: {
    byWorkOrder: (workOrderId: number) => `${config.apiBaseUrl}/api/workorder/notes/workorder/${workOrderId}`,
    create: `${config.apiBaseUrl}/api/workorder/notes`,
    update: (noteId: number) => `${config.apiBaseUrl}/api/workorder/notes/${noteId}`,
    delete: (noteId: number) => `${config.apiBaseUrl}/api/workorder/notes/${noteId}`,
  },

  // Dashboard
  dashboard: `${config.apiBaseUrl}/api/dashboard`,

  // Runsheet
  runsheet: `${config.apiBaseUrl}/api/runsheet`,

  // Technician Day Destinations
  dayDestinations: {
    list: (technicianId: number, date: string) => `${config.apiBaseUrl}/api/TechnicianDayDestination?technicianId=${technicianId}&date=${date}`,
    updateOrder: `${config.apiBaseUrl}/api/TechnicianDayDestination/order`,
    setCompleted: `${config.apiBaseUrl}/api/TechnicianDayDestination/complete`,
    updateLocation: `${config.apiBaseUrl}/api/TechnicianDayDestination/location`
  },

  // JSA (Job Safety Analysis)
  jsa: `${config.apiBaseUrl}/api/jsa`,
  
  // Tenants
  tenants: {
    get: (id: number) => `${config.apiBaseUrl}/api/tenant/${id}`,
    getByProperty: (propertyId: number) => `${config.apiBaseUrl}/api/tenant/property/${propertyId}`,
    getCurrent: `${config.apiBaseUrl}/api/tenant/current`,
    create: `${config.apiBaseUrl}/api/tenant`,
    update: (id: number) => `${config.apiBaseUrl}/api/tenant/${id}`,
    delete: (id: number) => `${config.apiBaseUrl}/api/tenant/${id}`,
  },

  // Messages
  messages: {
    base: `${config.apiBaseUrl}/api/message`,
    count: `${config.apiBaseUrl}/api/message/count`,
    get: (messageId: number) => `${config.apiBaseUrl}/api/message/${messageId}`,
    markAsRead: (messageId: number) => `${config.apiBaseUrl}/api/message/${messageId}/read`,
    markAsCompleted: (messageId: number, completed: boolean) => `${config.apiBaseUrl}/api/message/${messageId}/completed?completed=${completed}`,
  },
  
  // Lookups
  lookup: {
    workOrder: `${config.apiBaseUrl}/api/lookup/workorder`,
  },
  
  // Alarms
  alarms: {
    addExisting: (workOrderId: number) => `${config.apiBaseUrl}/api/workorder/${workOrderId}/alarms/existing`,
    addNew: (workOrderId: number) => `${config.apiBaseUrl}/api/workorder/${workOrderId}/alarms/new`,
    update: (alarmId: number) => `${config.apiBaseUrl}/api/workorder/alarms/${alarmId}`,
    delete: (alarmId: number) => `${config.apiBaseUrl}/api/workorder/alarms/${alarmId}`,
    uploadPhoto: (alarmId: number) => `${config.apiBaseUrl}/api/workorder/photos/alarm/${alarmId}/upload`,
    getPhotos: (alarmId: number) => `${config.apiBaseUrl}/api/workorder/photos/alarm/photos/${alarmId}`,
    deletePhoto: (photoId: number) => `${config.apiBaseUrl}/api/workorder/photos/alarm/${photoId}`,
    getByTempId: (workOrderId: number, tempId: string) => `${config.apiBaseUrl}/api/workorder/alarms/by-temp-id?workOrderId=${workOrderId}&tempId=${encodeURIComponent(tempId)}`,
  },

  // Payment
  payment: {
    createStripeSession: `${config.apiBaseUrl}/api/payment/stripe/create-session`,
    handleStripeSuccess: `${config.apiBaseUrl}/api/payment/stripe/success`,
    getInvoiceDetails: (workOrderId: number) => `${config.apiBaseUrl}/api/payment/invoice/${workOrderId}`,
    downloadInvoicePdf: (xeroInvoiceId: string) => `${config.apiBaseUrl}/api/payment/invoice/download/${xeroInvoiceId}`,
    getInvoiceViewUrl: (xeroInvoiceId: string) => `${config.apiBaseUrl}/api/payment/invoice/view-url/${xeroInvoiceId}`,
    createInvoice: `${config.apiBaseUrl}/api/payment/invoice/create`,
    syncInvoiceStatus: (xeroInvoiceId: string) => `${config.apiBaseUrl}/api/payment/invoice/sync/${xeroInvoiceId}`,
  },
  
  // Files - Updated for Azure Blob Storage with authentication
  files: {
    // Authenticated file serving endpoint
    serve: (filePath: string) => `${config.apiBaseUrl}/api/files/${filePath}`,
    // Get temporary access URL (optional, for direct blob access)
    temporaryUrl: (filePath: string, hours: number = 1) => `${config.apiBaseUrl}/api/files/url/${filePath}?expirationHours=${hours}`,
    // Generic file upload endpoint
    upload: (containerPath: string) => `${config.apiBaseUrl}/api/files/upload/${containerPath}`,
    // Work order photo upload
    uploadWorkOrderPhoto: (workOrderId: number) => `${config.apiBaseUrl}/api/workorder/photos/workorder/${workOrderId}/upload`,
    // Work order photo delete
    deleteWorkOrderPhoto: (photoId: number) => `${config.apiBaseUrl}/api/workorder/photos/${photoId}`,
    // JSA files (kept for compatibility)
    jsa: (technicianId: number, workOrderId: number) => `${config.apiBaseUrl}/api/jsa/technician/${technicianId}?workOrderId=${workOrderId}`,
  },
  
  // Utilities
  importAlarms: () => `${config.apiBaseUrl}/api/workorder/alarms/import`,

  // Geotab
  geotab: {
    all: `${config.apiBaseUrl}/api/geotab`,
    byTechnician: (technicianId: number) => `${config.apiBaseUrl}/api/geotab/technician/${technicianId}`,
  },
}

// Utility function to build full API URL
export const buildApiUrl = (endpoint: string): string => {
  if (endpoint.startsWith('http')) {
    return endpoint
  }
  return `${config.apiBaseUrl}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`
}

// Development logging
 